import { create } from 'zustand';

const useAppStore = create((set, get) => ({
  // ==========================================
  // Authentication State
  // ==========================================
  user: null,
  token: null,
  isAuthenticated: false,
  authLoading: true, // For initial auth check on app load

  // Auth actions
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setToken: (token) => set({ token }),
  setAuthLoading: (loading) => set({ authLoading: loading }),

  login: (user, token, refreshToken) => {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    set({ user, token, isAuthenticated: true, authLoading: false });
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    set({ user: null, token: null, isAuthenticated: false });
  },

  initAuth: () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    if (token && user) {
      set({ token, user, isAuthenticated: true, authLoading: false });
    } else {
      set({ authLoading: false });
    }
  },

  updateToken: (newToken, newRefreshToken) => {
    localStorage.setItem('token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    set({ token: newToken });
  },

  // ==========================================
  // Model selection state
  // ==========================================
  availableModels: [],
  selectedModel: null,
  defaultModel: 'google/gemini-2.5-flash',

  // Verification model selection state (for second pass)
  availableVerificationModels: [],
  selectedVerificationModel: null,
  defaultVerificationModel: 'openai/gpt-4o-mini',

  // File upload state
  uploadedFiles: [],
  uploadProgress: {},

  // Processing state
  isProcessing: false,
  processingStatus: {}, // { filename: { status, stage, message, progress, ... } }
  processingConfig: null, // { model, verificationModel, autoRerun }
  extractedData: [], // Array of { filename, metadata, transactions }

  // Validation state
  validationReport: null,
  hasValidationErrors: false,

  // Balance override state
  balanceOverrides: {}, // { [filename]: { opening: number, closing: number } }

  // Challenge rerun state
  rerunStatus: {}, // { [filename]: 'idle' | 'running' | 'complete' | 'failed' }

  // Export state
  exportId: null,
  exportReady: false,
  exportFiles: [],

  // File upload actions
  // Backend returns: { id, originalName, filename, path, size, mimeType, uploadedAt }
  addFiles: (files) =>
    set((state) => ({
      uploadedFiles: [
        ...state.uploadedFiles,
        ...files.map((f, idx) => ({
          id: f.id || `${Date.now()}-${idx}`,
          name: f.originalName || f.name,
          originalName: f.originalName || f.name,
          size: f.size,
          type: f.mimeType || f.type || f.mimetype,
          status: 'uploaded', // 'pending' | 'uploading' | 'uploaded' | 'error'
          serverFilename: f.filename || f.name,
        })),
      ],
    })),

  removeFile: (fileId) =>
    set((state) => ({
      uploadedFiles: state.uploadedFiles.filter((f) => f.id !== fileId),
    })),

  clearFiles: () =>
    set({
      uploadedFiles: [],
      uploadProgress: {},
      extractedData: [],
      validationReport: null,
      hasValidationErrors: false,
      balanceOverrides: {},
      rerunStatus: {},
      exportId: null,
      exportReady: false,
      exportFiles: [],
    }),

  updateFileStatus: (fileId, status) =>
    set((state) => ({
      uploadedFiles: state.uploadedFiles.map((f) =>
        f.id === fileId ? { ...f, status } : f
      ),
    })),

  setUploadProgress: (fileId, progress) =>
    set((state) => ({
      uploadProgress: { ...state.uploadProgress, [fileId]: progress },
    })),

  // Processing actions
  setProcessing: (isProcessing) => set({ isProcessing }),

  setProcessingConfig: (config) => set({ processingConfig: config }),

  setProcessingStatus: (filename, statusData) =>
    set((state) => ({
      processingStatus: {
        ...state.processingStatus,
        [filename]: typeof statusData === 'string'
          ? { status: statusData, stage: statusData, message: '', progress: 0 }
          : statusData,
      },
    })),

  // Batch update processing status for multiple files
  batchSetProcessingStatus: (updates) =>
    set((state) => ({
      processingStatus: { ...state.processingStatus, ...updates },
    })),

  addExtractedData: (data) =>
    set((state) => ({
      extractedData: [...state.extractedData, data],
    })),

  setExtractedData: (data) => set({ extractedData: data }),

  clearProcessingState: () =>
    set({
      isProcessing: false,
      processingStatus: {},
      processingConfig: null,
      extractedData: [],
    }),

  // Validation actions
  setValidationReport: (report) =>
    set({
      validationReport: report,
      hasValidationErrors: report?.errors?.length > 0,
    }),

  clearValidation: () =>
    set({
      validationReport: null,
      hasValidationErrors: false,
    }),

  // Balance override actions
  setBalanceOverride: (filename, opening, closing) =>
    set((state) => ({
      balanceOverrides: {
        ...state.balanceOverrides,
        [filename]: { opening, closing },
      },
    })),

  clearBalanceOverride: (filename) =>
    set((state) => {
      const newOverrides = { ...state.balanceOverrides };
      delete newOverrides[filename];
      return { balanceOverrides: newOverrides };
    }),

  clearAllBalanceOverrides: () => set({ balanceOverrides: {} }),

  getBalanceOverride: (filename) => get().balanceOverrides[filename] || null,

  // Challenge rerun actions
  setRerunStatus: (filename, status) =>
    set((state) => ({
      rerunStatus: { ...state.rerunStatus, [filename]: status },
    })),

  updateExtractedDataForFile: (filename, newData) =>
    set((state) => ({
      extractedData: state.extractedData.map((data) =>
        data.filename === filename
          ? { ...data, ...newData, metadata: newData.metadata || data.metadata, transactions: newData.transactions || data.transactions }
          : data
      ),
    })),

  clearRerunStatus: () => set({ rerunStatus: {} }),

  // Export actions
  setExportResult: (exportId, files) =>
    set({
      exportId,
      exportFiles: files,
      exportReady: true,
    }),

  clearExport: () =>
    set({
      exportId: null,
      exportFiles: [],
      exportReady: false,
    }),

  // Model actions
  setAvailableModels: (models, defaultModel) =>
    set({
      availableModels: models,
      defaultModel: defaultModel,
      selectedModel: get().selectedModel || defaultModel,
    }),

  setAvailableVerificationModels: (models, defaultModel) =>
    set({
      availableVerificationModels: models,
      defaultVerificationModel: defaultModel,
      selectedVerificationModel: get().selectedVerificationModel || defaultModel,
    }),

  setSelectedModel: (model) => set({ selectedModel: model }),

  setSelectedVerificationModel: (model) => set({ selectedVerificationModel: model }),

  getSelectedModel: () => get().selectedModel || get().defaultModel,

  getSelectedVerificationModel: () => get().selectedVerificationModel || get().defaultVerificationModel,

  // Utility getters
  getFileCount: () => get().uploadedFiles.length,

  getProcessedCount: () =>
    Object.values(get().processingStatus).filter((s) => s === 'complete').length,

  getTotalTransactions: () =>
    get().extractedData.reduce((sum, d) => sum + (d.transactions?.length || 0), 0),
}));

export default useAppStore;
